// Sets up each pixmoto popover anchor: play icon, banner sizing, click to
// open, and the media warm armed on hover, touch or focus.
import { must } from "@/common/utils/must";
import ExperienceAttributes from "@/modules/ExperienceAttributes";
import urlUtils from "@/common/utils/urlUtils";
import { preloadImage, warmDefaultPlayIcons } from "@/modules/assetImageCache";
import {
  isMediaWarmStageReady,
  startMediaWarm,
} from "@/modules/experiencePrefetchWarm";
import { runPrefetch } from "@/popover/anchorPrefetch";
import { cacheKeyFromAttrs } from "@/popover/overlayUrls";
import type {
  PopoverAnchor,
  PopoverOpener,
  PrefetchBucket,
  InflightRegistry,
} from "@/popover/types";

export function awaitPrefetchedApiJson(
  cacheKey: string,
  attrs: ExperienceAttributes | null | undefined,
): Promise<unknown> {
  if (attrs) {
    runPrefetch(attrs);
  }

  return new Promise(resolve => {
    const pm = window.__pixmoto || {};
    const bucket: PrefetchBucket = pm.prefetchedExperiences || {};
    const inflight: InflightRegistry = pm._prefetchInflight || {};
    let entry = bucket[cacheKey];

    if (entry && entry.status === "ok") {
      resolve(entry.data);
      return;
    }
    if (entry && entry.status === "error") {
      resolve(null);
      return;
    }

    const started = Date.now();
    const maxMs = 20000;
    const timer = setInterval(() => {
      entry = bucket[cacheKey];
      if (entry && entry.status === "ok") {
        clearInterval(timer);
        resolve(entry.data);
        return;
      }
      if (entry && entry.status === "error") {
        clearInterval(timer);
        resolve(null);
        return;
      }
      if (!inflight[cacheKey] && Date.now() - started > maxMs) {
        clearInterval(timer);
        resolve(null);
      }
    }, 50);
  });
}

export function armWarmOnIntent(
  anchor: PopoverAnchor,
  attrs: ExperienceAttributes,
) {
  const cacheKey = cacheKeyFromAttrs(attrs);
  if (!cacheKey) {
    return;
  }
  let armed = false;
  // Start the heavy video warm only when the user signals intent —
  // hover (desktop), touch, or keyboard focus. Idempotent per anchor.
  const warm = function () {
    if (armed) {
      return;
    }
    armed = true;
    anchor.removeEventListener("mouseenter", warm);
    anchor.removeEventListener("touchstart", warm);
    anchor.removeEventListener("focusin", warm);
    kickMediaWarmInBackground(cacheKey, attrs);
  };
  anchor.addEventListener("mouseenter", warm);
  anchor.addEventListener("touchstart", warm, { passive: true });
  anchor.addEventListener("focusin", warm);
}

export function kickMediaWarmInBackground(
  cacheKey: string,
  attrs: ExperienceAttributes | null | undefined,
) {
  if (attrs) {
    runPrefetch(attrs);
  }
  const warmOpts = attrs ? { playServer: attrs.getPlayServer() } : undefined;
  const pm = window.__pixmoto || {};
  const bucket: PrefetchBucket = pm.prefetchedExperiences || {};
  const entry = bucket[cacheKey];
  if (entry && entry.status === "ok" && entry.data) {
    if (!isMediaWarmStageReady(cacheKey, "ready")) {
      startMediaWarm(cacheKey, entry.data, warmOpts);
    }
    return;
  }
  awaitPrefetchedApiJson(cacheKey, attrs).then(apiJson => {
    if (apiJson && !isMediaWarmStageReady(cacheKey, "ready")) {
      startMediaWarm(cacheKey, apiJson, warmOpts);
    }
  });
}

export function setupPixmotoAnchor(
  popover: PopoverOpener,
  anchor: PopoverAnchor,
) {
  if (anchor.pixmoto) {
    return;
  }

  const attrs = ExperienceAttributes.fromCombinedAttributes(
    anchor,
    urlUtils.querySettings(),
    true,
  );
  const hasPlayIcon = attrs.hasPlayIcon();
  let img: HTMLImageElement | undefined;

  anchor.pixmoto = anchor.pixmoto || ({} as { attrs: ExperienceAttributes });
  anchor.pixmoto.attrs = attrs;

  armWarmOnIntent(anchor, attrs);

  anchor.onclick = function (this: PopoverOpener, event: MouseEvent) {
    this.popover(anchor, attrs, false);
    return false;
  }.bind(popover);

  anchor.style.position = "relative";
  anchor.style.display = "inline-block";
  anchor.style.borderStyle = "none";

  const banners = anchor.getElementsByTagName("img");
  let banner: HTMLImageElement | undefined;

  if (banners.length === 0) {
    const bannerSource = attrs.bannerSource();
    if (bannerSource) {
      banner = document.createElement("img");
      banner.src = String(bannerSource);
      banner.setAttribute("width", "100%");
      anchor.appendChild(banner);
    }
  } else {
    banner = banners[0];
  }

  let reuseMappedPlayIcon: HTMLImageElement | null = null;
  const defaultPlaySrc = hasPlayIcon ? attrs.getPlayIconUrl() : undefined;
  const hoverPlaySrc = hasPlayIcon ? attrs.getPlayIconHoverUrl() : undefined;

  if (hasPlayIcon) {
    anchor.style.lineHeight = "0";

    reuseMappedPlayIcon = anchor.querySelector<HTMLImageElement>(
      ".pixmoto-mapped-play-icon",
    );

    warmDefaultPlayIcons(attrs.getSharedServer());
    if (defaultPlaySrc) {
      preloadImage(defaultPlaySrc);
    }
    if (hoverPlaySrc) {
      preloadImage(hoverPlaySrc);
    }

    if (reuseMappedPlayIcon) {
      img = reuseMappedPlayIcon;
      img.style.borderStyle = "none";
      if (!img.style.position) {
        img.style.position = "absolute";
      }
      if (
        defaultPlaySrc &&
        reuseMappedPlayIcon.getAttribute("src") !== defaultPlaySrc
      ) {
        reuseMappedPlayIcon.setAttribute("src", String(defaultPlaySrc));
      }
    } else {
      const created = document.createElement("img");
      img = created;
      img.style.position = "absolute";
      img.style.opacity = "0";
      img.style.borderStyle = "none";
      anchor.appendChild(img);
      if (defaultPlaySrc) {
        preloadImage(defaultPlaySrc).then(() => {
          if (!created.src) {
            created.src = String(defaultPlaySrc);
          }
        });
      }
    }

    const playIconEl = img;
    if (defaultPlaySrc && hoverPlaySrc) {
      anchor.addEventListener("mouseenter", () => {
        playIconEl.src = String(hoverPlaySrc);
      });
      anchor.addEventListener("mouseleave", () => {
        playIconEl.src = String(defaultPlaySrc);
      });
    }
  }

  if (banner) {
    banner.style.borderStyle = "none";
    banner.style.borderRadius = String(attrs.bannerRadius() || "0px");

    if (hasPlayIcon) {
      const tryPackAnchorIcon = function (this: PopoverOpener) {
        if (!banner.complete) {
          return;
        }
        if (!reuseMappedPlayIcon && img && !img.complete && !img.src) {
          return;
        }
        setupPixmotoAnchorSizes(anchor);
      }.bind(popover);

      banner.onload = tryPackAnchorIcon;

      const playLoads: Promise<HTMLImageElement | void>[] = [];
      if (defaultPlaySrc) {
        playLoads.push(preloadImage(defaultPlaySrc));
      }
      if (hoverPlaySrc) {
        playLoads.push(preloadImage(hoverPlaySrc));
      }
      Promise.all(playLoads).then(() => {
        if (img && defaultPlaySrc && !img.src) {
          img.src = String(defaultPlaySrc);
        }
        tryPackAnchorIcon();
      });

      tryPackAnchorIcon();
      requestAnimationFrame(() => {
        requestAnimationFrame(tryPackAnchorIcon);
      });
      setTimeout(tryPackAnchorIcon, 50);
      setTimeout(tryPackAnchorIcon, 250);
    }
  }
}

export function setupPixmotoAnchorSizes(anchor: PopoverAnchor) {
  const attrs = must(anchor.pixmoto, "popover anchor setup").attrs;

  if (attrs.hasPlayIcon()) {
    const images = anchor.getElementsByTagName("img");
    const banner = images.length > 0 ? images[0] : null;
    const watermark = images.length > 1 ? images[1] : null;
    let bannerWidth: number;
    let bannerHeight: number;
    let iconSize: { width: number; height: number } | undefined;

    if (banner && watermark) {
      bannerWidth = banner.clientWidth;
      bannerHeight = banner.clientHeight;

      if (attrs.getCustomPlayIconUrl()) {
        iconSize = attrs.getCustomPlayIconSize();
        if (!iconSize) {
          iconSize = {
            width: watermark.naturalWidth,
            height: watermark.naturalHeight,
          };
        }
      } else {
        iconSize = attrs.getPlayIconSize();
      }

      watermark.style.width = `${iconSize.width}px`;
      watermark.style.height = `${iconSize.height}px`;
      watermark.style.left = `${(bannerWidth - iconSize.width) / 2}px`;
      watermark.style.bottom = `${(bannerHeight - iconSize.height) / 2}px`;
      watermark.style.opacity = "1";
    }
  }
}
